// Product-aware sidebar (260 px, collapsible to a 72 px icon rail). Renders the
// active app's role-filtered nav: product header with icon tile, grouped items
// with 11 px uppercase group labels, active item = soft fill + 2 px
// product-colour rail. Arrow keys move between items; the collapsed rail has
// styled tooltips. Off-canvas on phones (AppShell's MobileNav dialog). Rows are
// 36 px with a fine pointer and 44 px under a coarse one (`coarse:`). Footer:
// user + role + "Switch product".

import { Fragment, useCallback, type KeyboardEvent, type ReactNode } from "react";
import { preloadHandlers } from "@/app/routePreload";
import { NavLink } from "react-router-dom";
import { ChevronLeft, ChevronRight, LayoutGrid, X } from "lucide-react";

import { useAuth } from "@/lib/auth/AuthProvider";
import { visibleGroups, type ConsoleApp, type ConsoleNavItem } from "@/app/products";
import { personaForRole } from "@/app/orgConstants";
import { ProductIcon, Tooltip } from "@/app/console/ui";

interface SidebarProps {
  app: ConsoleApp;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSwitchProduct?: () => void;
  /** Rendered as a close (×) control — used by the phone off-canvas dialog. */
  onClose?: () => void;
  /** Rendered under the product header when expanded (the phone nav drawer's site row). */
  topSlot?: ReactNode;
}

const ROW = "h-9 coarse:h-11";

export function Sidebar({ app, collapsed, onToggleCollapse, onSwitchProduct, onClose, topSlot }: SidebarProps) {
  const { roles, user } = useAuth();
  const groups = visibleGroups(app, roles);

  // Roving arrow-key focus across every nav link in the sidebar.
  const onNavKey = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const links = Array.from(e.currentTarget.querySelectorAll<HTMLAnchorElement>("a[href]"));
    if (links.length === 0) return;
    const i = links.findIndex((l) => l === document.activeElement);
    e.preventDefault();
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? links.length - 1 : e.key === "ArrowDown" ? (i + 1) % links.length : (i - 1 + links.length) % links.length;
    links[next]?.focus();
  }, []);

  return (
    <div className="flex h-full select-none flex-col bg-surface-1">
      {/* ----- Header: current product ----- */}
      <div className={`flex h-header shrink-0 items-center border-b border-line ${collapsed ? "justify-center px-2" : "gap-2 px-3"}`}>
        {collapsed ? (
          <Tooltip content={`${app.name} — expand sidebar`} side="right">
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-label="Expand sidebar"
              className="flex h-10 w-10 items-center justify-center rounded-control transition-colors duration-fast hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:h-11 coarse:w-11"
            >
              <ProductIcon icon={app.icon} accent={app.accent} size="sm" />
            </button>
          </Tooltip>
        ) : (
          <>
            <div className="flex min-w-0 flex-1 items-center gap-2.5" aria-label={`Current product: ${app.name}`}>
              <ProductIcon icon={app.icon} accent={app.accent} size="sm" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold leading-5 text-text-1">{app.name}</span>
                <span className="block truncate text-[11px] leading-4 text-text-3">{app.tagline}</span>
              </span>
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control text-text-2 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 lg:hidden"
                aria-label="Close navigation"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-control text-text-3 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:h-11 coarse:w-11 lg:flex"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {!collapsed && topSlot && <div className="shrink-0 border-b border-line px-2 py-1.5">{topSlot}</div>}

      {/* ----- Nav ----- */}
      {collapsed ? (
        <nav
          className="flex flex-1 flex-col items-center gap-1 overflow-y-auto overflow-x-hidden py-3"
          aria-label={`${app.name} navigation`}
          onKeyDown={onNavKey}
        >
          {groups.map((group, gi) => (
            <Fragment key={group.id}>
              {gi > 0 && <div className="my-1.5 h-px w-7 bg-line-strong" aria-hidden="true" />}
              {group.items.map((item) => (
                <SidebarItem key={item.to} item={item} collapsed accent={app.accent} />
              ))}
            </Fragment>
          ))}
          <div className="mt-auto pt-2">
            <Tooltip content="Expand sidebar" side="right">
              <button
                type="button"
                onClick={onToggleCollapse}
                aria-label="Expand sidebar"
                className="flex h-8 w-10 items-center justify-center rounded-control text-text-3 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:h-11 coarse:w-11"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        </nav>
      ) : (
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4" aria-label={`${app.name} navigation`} onKeyDown={onNavKey}>
          {groups.map((group) => (
            <div key={group.id}>
              <div className="pb-micro px-3 pb-1.5 text-text-3">{group.label}</div>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <SidebarItem key={item.to} item={item} collapsed={false} accent={app.accent} />
                ))}
              </div>
            </div>
          ))}
        </nav>
      )}

      {/* ----- User footer ----- */}
      {user && (
        <div className={`border-t border-line ${collapsed ? "px-2 py-3" : "px-3 py-3"}`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Tooltip content={`${user.email} · ${user.role.replace(/_/g, " ")}`} side="right">
                <span className="inline-flex" tabIndex={-1}>
                  <Avatar email={user.email} role={user.role} />
                </span>
              </Tooltip>
              {onSwitchProduct && (
                <Tooltip content="Switch product · ⌘1–7" side="right">
                  <button
                    type="button"
                    onClick={onSwitchProduct}
                    aria-label="Switch product"
                    className="flex h-9 w-10 items-center justify-center rounded-control text-text-3 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:h-11 coarse:w-11"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                </Tooltip>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5 px-1">
                <Avatar email={user.email} role={user.role} />
                <div className="min-w-0 text-xs leading-4">
                  <div className="truncate text-text-1">{user.email}</div>
                  <div className="truncate capitalize text-text-3">{user.role.replace(/_/g, " ")}</div>
                </div>
              </div>
              {onSwitchProduct && (
                <button
                  type="button"
                  onClick={onSwitchProduct}
                  className={`flex ${ROW} w-full items-center justify-center gap-2 rounded-control border border-line-strong text-[13px] font-medium text-text-2 transition-colors duration-fast hover:border-white/25 hover:bg-white/[0.04] hover:text-text-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60`}
                >
                  <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                  Switch product
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Avatar({ email, role }: { email: string; role: string }) {
  const accent = personaForRole(role).accent;
  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
      style={{ backgroundColor: `${accent}22`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}55` }}
      aria-hidden="true"
    >
      {email.slice(0, 2).toUpperCase()}
    </div>
  );
}

function SidebarItem({ item, collapsed, accent }: { item: ConsoleNavItem; collapsed: boolean; accent: string }) {
  const Icon = item.icon;
  const rail = item.accent ?? accent;

  // ---------- Collapsed rail (centered icon tiles) ----------
  if (collapsed) {
    if (item.soon) {
      return (
        <Tooltip content={`${item.label} — coming soon`} side="right">
          <span
            aria-label={`${item.label} — coming soon`}
            className="flex h-10 w-10 cursor-not-allowed items-center justify-center rounded-control text-text-4"
          >
            <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
        </Tooltip>
      );
    }
    return (
      <Tooltip content={item.hint ? `${item.label} · ${item.hint}` : item.label} side="right">
        <NavLink
          to={item.to}
          end={item.end}
          {...preloadHandlers(item.to)}
          aria-label={item.label}
          className={({ isActive }) =>
            [
              "group relative flex h-10 w-10 items-center justify-center rounded-control transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 coarse:h-11 coarse:w-11",
              isActive ? "bg-white/[0.06] text-text-1" : "text-text-2 hover:bg-white/[0.04] hover:text-text-1",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <>
              {isActive && (
                <span className="absolute -left-4 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r transition-opacity duration-fast group-focus-visible:opacity-0" style={{ backgroundColor: rail }} aria-hidden="true" />
              )}
              <Icon className="h-[18px] w-[18px]" aria-hidden="true" style={item.accent ? { color: item.accent } : undefined} />
            </>
          )}
        </NavLink>
      </Tooltip>
    );
  }

  // ---------- Expanded panel (icon + label) ----------
  if (item.soon) {
    return (
      <span
        aria-label={`${item.label} — coming soon`}
        className={`flex ${ROW} cursor-not-allowed items-center gap-3 rounded-control px-3 text-sm text-text-4`}
      >
        <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
        <span className="flex-1 truncate">{item.label}</span>
        <span className="pb-micro rounded bg-white/[0.04] px-1.5 py-0.5 text-text-4">soon</span>
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      {...preloadHandlers(item.to)}
      className={({ isActive }) =>
        [
          `group relative flex ${ROW} items-center gap-3 rounded-control px-3 text-sm transition-[background-color,color,transform] duration-fast ease-pb focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60`,
          isActive
            ? "bg-white/[0.06] font-medium text-text-1"
            : "text-text-2 hover:translate-x-px hover:bg-white/[0.04] hover:text-text-1",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute -left-3 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r transition-opacity duration-fast group-focus-visible:opacity-0" style={{ backgroundColor: rail }} aria-hidden="true" />
          )}
          {item.accent ? (
            <ProductIcon icon={Icon} accent={item.accent} size="xs" style={{ width: 24, height: 24, borderRadius: 6 }} />
          ) : (
            <Icon
              className={`h-[18px] w-[18px] shrink-0 transition-colors duration-fast ${isActive ? "text-text-1" : "text-text-3 group-hover:text-text-1"}`}
              aria-hidden="true"
            />
          )}
          <span className="flex-1 truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}
